import React, { useEffect, useState } from "react";
import Container from "../components/Container.jsx";
import { renderMarkdown } from "../lib/markdown.js";
import useArticles from "../hooks/useArticles.js";

// 🔧 simple helper: remove YAML front-matter between first two --- lines
function stripFrontMatter(raw) {
  if (!raw) return "";
  // remove: --- ... --- (including newlines), once, at the very start
  return raw.replace(/^---[\s\S]*?---\s*/, "");
}

function copyLink() {
  try {
    navigator.clipboard.writeText(window.location.href);
    alert("Link copied to clipboard");
  } catch {}
}

export default function Article({ slug }) {
  const articles = useArticles();
  const meta = articles.find((a) => a.slug === slug) || null;
  const [content, setContent] = useState("");

  useEffect(() => {
    const base = import.meta?.env?.BASE_URL ?? "/";
    const urlA = `${base}content/articles/${slug}.md`;
    const urlB = `content/articles/${slug}.md`;

    const tryUrl = async (u) => {
      const r = await fetch(u, { cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status} for ${u}`);
      const raw = await r.text();
      const cleaned = stripFrontMatter(raw);
      return cleaned || raw;
    };

    (async () => {
      try {
        setContent(await tryUrl(urlA));
      } catch {
        try {
          setContent(await tryUrl(urlB));
        } catch {
          setContent("# Article not found");
        }
      }
    })();
  }, [slug]);

  const prettyDate = meta?.date
    ? new Date(meta.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <main>
      {meta?.image && (
        <section className="bg-white">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-6">
            <figure className="overflow-hidden rounded-3xl shadow-md ring-1 ring-slate-200">
              <img
                src={meta.image}
                alt={meta.title || ""}
                className="h-[320px] w-full object-cover sm:h-[420px]"
              />
            </figure>
          </div>
        </section>
      )}

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6 flex items-center justify-between gap-3">
            <a
              href="#/news"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#003366] hover:text-[#003366]"
            >
              <span className="-ml-1">◀</span> Back to News
            </a>
            <button
              onClick={copyLink}
              className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm text-[#003366] hover:bg-slate-50"
            >
              Share
            </button>
          </div>

          {meta && (
            <header className="mb-6">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                {meta.category && (
                  <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-[#003366]">
                    {meta.category}
                  </span>
                )}
                <span className="text-[#003366]">•</span>
                {prettyDate && (
                  <time className="text-[#003366]" dateTime={meta.date}>
                    {prettyDate}
                  </time>
                )}
                {meta.readTime ? (
                  <>
                    <span className="text-[#003366]">•</span>
                    <span className="text-[#003366]">
                      {meta.readTime} min read
                    </span>
                  </>
                ) : null}
              </div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#003366]">
                {meta.title}
              </h1>
            </header>
          )}

          <article className="prose prose-slate max-w-none prose-h2:mt-10 prose-h2:mb-3 prose-h3:mt-8 prose-h3:mb-2 prose-p:leading-7 prose-p:mb-6 prose-li:mb-2">
            <div
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
            />
          </article>

          <div className="mt-10 rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200">
            <h3 className="text-lg font-semibold text-[#003366]">
              Speak to Ethira
            </h3>
            <p className="mt-1 text-[#003366]">
              Want help turning this insight into an actionable plan? Our
              advisory team can help.
            </p>
            <a
              href="#enquire"
              className="mt-4 inline-flex items-center rounded-2xl bg-[#003366] px-4 py-2 text-white font-semibold hover:bg-slate-800"
            >
              Enquire now →
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
